export const COPY_EXAMPLES_CHANNELS = ["email", "sms", "push", "ads"] as const;
export type CopyExamplesChannel = (typeof COPY_EXAMPLES_CHANNELS)[number];

export type CopyExamplesResolvedSource = {
  table: string;
  columns: {
    id: string;
    name: string | null;
    subject: string | null;
    preheader: string | null;
    body: string;
    updated_at: string;
    is_draft: string | null;
    is_archived: string | null;
  };
};

// Bounded because the free-text row-filter hint flows into the ingestion developer prompt.
export const COPY_EXAMPLES_ROW_FILTER_NOTE_MAX_LENGTH = 2000;
export const COPY_EXAMPLES_MAX_EXAMPLES = 120;
export const COPY_EXAMPLES_MAX_BODY_LENGTH = 50_000;
// `raw_body` preserves the machine-extracted original (typically full email HTML) for audit only;
// the HTML-stripped `body` is used for generation. Raw HTML routinely exceeds the body cap, so it
// gets a larger ceiling and is truncated (not rejected) at write time — losing an audit tail is
// acceptable, failing an ingestion is not. Aggregate size is bounded by SCRATCH_MAX_BYTES.
export const COPY_EXAMPLES_MAX_RAW_BODY_LENGTH = 100_000;
export const COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH = 2_000;
// Mirrors MAX_IMAGES_PER_AD in the Meta Ad Library ingestion tools: the creatives one ad
// contributes are capped there, so a persisted example never carries more.
export const COPY_EXAMPLES_MAX_IMAGE_URLS = 5;
export const COPY_EXAMPLES_SCRATCH_FILENAME = "copy-examples-scratch.json";
export const COPY_EXAMPLES_MANUAL_INPUT_MAX_BYTES = 2 * 1024 * 1024;
export const COPY_EXAMPLES_SCRATCH_MAX_BYTES = 8 * 1024 * 1024;

export type CopyExamplesExtractionParams = {
  lookback_days: number;
  min_sends: number | null;
  target_count: number;
  // Free-text, NON-executable scoping hint the agent interprets when authoring its own extraction
  // SQL; never run as SQL by the server. null when no guidance was supplied (incl. legacy rows).
  row_filter_note: string | null;
};

// How the body was recovered. "text" = HTML/text fields (default, incl. alt-text
// resolution); "image_ocr" = reconstructed from a whole-email screenshot.
export type CopyExampleSourceModality = "text" | "image_ocr";

export type CopyExample = {
  id: string;
  source_id: string | null;
  subject?: string;
  preheader?: string;
  title?: string;
  cta?: string;
  body: string;
  raw_body?: string;
  campaign_name: string | null;
  sent_at: string | null;
  excluded: boolean;
  exclusion_reason: string | null;
  source_modality?: CopyExampleSourceModality;
  // Source ad creative image URLs (ads channel only), carried so the review UI can render the
  // actual ad next to the copy transcribed from it. Signed CDN URLs that may expire; the UI must
  // degrade gracefully when one no longer loads.
  image_urls?: string[];
};

export type CopyExamplesTarget = {
  // Destination the examples are for at generation time. null = channel-generic (all destinations).
  destination_id: string | null;
};

// How the set was populated. fivetran/warehouse carry a resolved_source and are refreshable;
// manual is a one-off paste/upload, not refreshable. meta_ad_library reads the brand's public
// Meta Ad Library page (no warehouse involved) and is refreshable by re-fetching the page.
export type CopyExamplesSource =
  | {
      kind: "fivetran";
      fivetran_sync_id: string;
      resolved_source: CopyExamplesResolvedSource;
    }
  | {
      kind: "warehouse";
      connection_id: string; // Hightouch source/connection id
      resolved_source: CopyExamplesResolvedSource;
    }
  | { kind: "manual" }
  | {
      kind: "meta_ad_library";
      // Meta Ad Library page id of the brand's advertiser page.
      page_id: string;
      // Display name of the page as picked in the wizard; null for legacy rows.
      page_name: string | null;
      // ISO country code the ads were fetched for.
      country: string;
    };

export type CopyExamplesContent = {
  // Server-authoritative, never authored by the agent. Defaults to current version on parse so
  // legacy rows read back as v1. Bumped when the stored shape changes incompatibly.
  schema_version: number;
  channel: CopyExamplesChannel;
  target: CopyExamplesTarget;
  source: CopyExamplesSource;
  extraction_params: CopyExamplesExtractionParams;
  examples: CopyExample[];
};

// Intermediate (--keep-image-urls) scratch metadata; dropped after the resolve pass.
export type CopyExamplesScratchImage = {
  url: string;
  alt: string;
  position: number;
};

export type CopyExamplesScratchRecord = Omit<
  CopyExample,
  "excluded" | "exclusion_reason"
> & {
  body_fingerprint: string;
  images?: CopyExamplesScratchImage[];
};

export type CopyExamplesScratchFile = {
  examples: CopyExamplesScratchRecord[];
};

export type CopyExamplesReviewJudgment = {
  scratch_id: string;
  excluded: boolean;
  exclusion_reason: string | null;
  // Presentation cleanup of the scratch body: may remove boilerplate and reflow
  // punctuation/whitespace, but cannot add, rewrite, or reorder words or URLs. When present, it
  // becomes the persisted `body` (scratch body kept as `raw_body`); null/absent uses it verbatim.
  cleaned_body?: string | null;
};

export type CopyExamplesReviewOutputContent = {
  summary: string;
  name: string;
  description: string;
  channel: CopyExamplesChannel;
  source: CopyExamplesSource;
  extraction_params: CopyExamplesExtractionParams;
  scratch_file: {
    filePath: string;
  };
  examples: CopyExamplesReviewJudgment[];
};
